<template>
  <div>
    <b-form-row>
      <partial-checkbox
        :allow-selection="allowSelection"
        :selected="partiallySelection.selected"
        :state="partiallySelection.state"
        @change="(data) => onPartialInvoiceChange(data)"
      />
      <b-col cols="12" :md="6" class="mb-2">
        <b-form-group class="mb-0">
          <multiselect
            v-e2e:selectProduct
            v-model="selectProduct"
            track-by="id"
            label="name"
            :show-labels="false"
            :disabled="!editProducts || readonly"
            :options="filteredOptions"
            :internal-search="false"
            @search-change="searchProducts"
          >
            <template #option="{ option }">
              {{ option.name }}
              <br />
              <b-badge v-if="option.item_id" variant="light" class="mt-2">
                SKU: {{ option.item_id }}
              </b-badge>
              <br />
              <b-badge v-if="option.productType" variant="light" class="mt-2">
                {{
                  option.productType[`value_${locale}`] ||
                  option.productType.value_de
                }}
              </b-badge>
            </template>
          </multiselect>
        </b-form-group>
      </b-col>
      <b-col cols="3" md class="mb-2">
        <b-form-input
          v-e2e:productQuantity
          v-model="quantity"
          type="number"
          :min="minQuantity"
          :max="maxQuantity"
          :disabled="!editProducts || readonly || minQuantity == maxQuantity"
        />
      </b-col>

      <b-col class="mb-2 gid-tiny-col">
        <b-form-input
          value="×"
          :readonly="true"
          :plaintext="true"
          tabindex="-1"
        />
      </b-col>
      <b-col class="mb-2" v-if="!hidePriceListing">
        <b-form-input
          v-e2e:productPriceListing
          class="text-right"
          v-model="price_listing"
          :readonly="!editPriceListing || readonly"
          :style="price_listing_style"
          title="Listing price"
          v-b-tooltip.hover
        />
      </b-col>
      <b-col class="mb-2" v-if="!hidePriceSelling">
        <b-form-input
          v-e2e:productPriceSelling
          class="text-right"
          v-model="price_selling"
          :readonly="!editPriceSelling || readonly"
          :style="price_selling_style"
          title="Selling price"
          v-b-tooltip.hover
        />
      </b-col>
      <b-col class="mb-2" v-if="!hidePricePurchasing">
        <b-form-input
          v-e2e:productPricePurchasing
          class="text-right"
          v-model="price_purchasing"
          :readonly="!editPricePurchasing || readonly"
          title="Purchasing price"
          v-b-tooltip.hover
        />
      </b-col>

      <b-col class="mb-2 gid-tiny-col" v-if="editProducts && !readonly">
        <b-button
          v-e2e:productRemove
          variant="outline-secondary"
          title="Remove"
          class="btn-block"
          @click="deleteSelectedProduct(elementIndex)"
        >
          <i class="material-icons">delete</i>
        </b-button>
      </b-col>
    </b-form-row>
    <b-form-row v-if="context == 'admin'">
      <b-col md>
        <b-form-group label="Supplier Type">
          <multiselect
            v-model="supplierType"
            :disabled="!editProducts || readonly"
            :show-labels="false"
            :options="supplierTypes"
          />
        </b-form-group>
      </b-col>
      <b-col md v-if="supplierType == 'gid'">
        <b-form-group label="Supplier">
          <multiselect
            v-model="supplier"
            :disabled="!editProducts || readonly || suppliers.length == 1"
            :show-labels="false"
            track-by="name"
            label="name"
            :options="suppliers"
          />
        </b-form-group>
      </b-col>
      <b-col md>
        <b-form-group label="Invoicing target">
          <multiselect
            v-model="invoicingTarget"
            :disabled="!editInvoicingTarget || readonly"
            :show-labels="false"
            :options="invoicingTargets"
          />
        </b-form-group>
      </b-col>
    </b-form-row>
    <b-form-row v-if="editProducts || serial_number || chargepoint_number">
      <b-col md>
        <b-form-group :label="$t('inputs.products.serial-number')">
          <b-form-input
            v-e2e:productSerialNumber
            v-model="serial_number"
            type="text"
            :disabled="!editProducts || readonly"
          />
        </b-form-group>
      </b-col>
      <b-col md>
        <b-form-group :label="$t('inputs.products.chargepoint-number')">
          <b-form-input
            v-e2e:productChargepointNumber
            v-model="chargepoint_number"
            type="text"
            :disabled="!editProducts || readonly"
          />
        </b-form-group>
      </b-col>
    </b-form-row>
    <span class="d-block mb-2" v-if="getProductOrderTrackingLink">
      <b-icon-box class="mr-2" />
      Tracking Link:
      <a :href="getProductOrderTrackingLink" target="_blank">
        {{ getProductOrderTrackingID }}
      </a>
    </span>

    <span class="d-block mb-2" v-if="productUrl">
      <a :href="productUrl" target="_blank">
        {{ $t('inputs.products.url') }}
      </a>
    </span>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BaseInput from './mixins/BaseInput';
import LimitServiceDefinitions from './mixins/LimitServiceDefinitions';
import Multiselect from 'vue-multiselect';
import PartialCheckbox, { PartialStateEnum } from './common/PartialCheckbox';
import {
  ServiceLinkTypeEnum,
  SupplierTypesEnum,
  GeneralInvoicingTargetEnum,
} from '@gid/models';
import { SelectActionsEnum } from '@gid/models/dist/entities/job-selection';
import { BIconBox } from 'bootstrap-vue';
import { loadPartialSelection } from '../../utils/partial_selection';

import {
  PRODUCTS_SELECTED_ITEM_DELETE,
  PRODUCTS_SELECTED_SET,
  PRODUCT_OPPORTUNITIES_FETCH,
  PRODUCT_PRESELECT_AUTOMATION,
} from '../../store/products.module/types';
import {
  SERVICE_SELECTED_ADD,
  SERVICE_SELECTED_ADD_QUANTITY,
  SERVICE_SELECTED_ITEM_UNREF,
  SERVICE_SELECTED_REFRESH,
  SERVICE_PRESELECT_AUTOMATION_BY_PRODUCT,
} from '../../store/services.module/types';

function nullOrUndefined(value) {
  return value === null || value === undefined || value === '';
}

export default {
  components: {
    Multiselect,
    BIconBox,
    PartialCheckbox,
  },
  mixins: [BaseInput, LimitServiceDefinitions],
  props: {
    allowDuplicateServices: {
      type: Boolean,
      default: false,
    },
    elementIndex: Number,
    editProducts: {
      type: Boolean,
      default: true,
    },
    jobId: {
      type: String,
      default: 'new',
    },
    hidePriceListing: Boolean,
    hidePricePurchasing: Boolean,
    hidePriceSelling: Boolean,
    editPriceListing: Boolean,
    editPriceSelling: Boolean,
    editPricePurchasing: Boolean,
    editInvoicingTarget: Boolean,
    typesExclude: Array,
    typesInclude: Array,
    context: String,
    orderDetails: null,
    allowSelection: {
      type: Boolean,
      default: false,
    },
    selections: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      previousProductId: '',
      minQuantity: 1,
      maxQuantity: 30,
      productFilter: '',
      supplierTypes: Object.values(SupplierTypesEnum),
      invoicingTargets: Object.values(GeneralInvoicingTargetEnum),
    };
  },
  computed: {
    ...mapGetters(['locale', 'useExternalNames']),
    getProductOrderTrackingLink() {
      return this.orderDetails?.find(({ items }) =>
        items?.includes(this.selectProduct?.name),
      )?.url;
    },
    getProductOrderTrackingID() {
      return this.orderDetails?.find(({ items }) =>
        items?.includes(this.selectProduct?.name),
      )?.trackingID;
    },
    activeProduct() {
      return this.$store.getters.activeProduct({
        jobId: this.jobId,
        elementIndex: this.elementIndex,
      });
    },
    activeProductId() {
      return this.activeProduct.product;
    },
    activeProductDefinition() {
      return this.$store.getters.productDefinition(this.activeProductId);
    },
    activeProductSuppliers() {
      return (
        this.activeProductDefinition?.suppliers?.filter(
          (supplier) => supplier.id,
        ) || []
      );
    },
    productOptions() {
      let options = this.$store.getters.productOptions(this.jobId);
      if (Array.isArray(this.typesExclude) && this.typesExclude.length > 0) {
        options = options.filter(
          ({ productType }) => !this.typesExclude.includes(productType?.id),
        );
      }
      if (Array.isArray(this.typesInclude) && this.typesInclude.length > 0) {
        options = options.filter(({ productType }) =>
          this.typesInclude.includes(productType?.id),
        );
      }
      return options;
    },
    filteredOptions() {
      const pattern = new RegExp(this.productFilter, 'i');
      return this.productOptions.filter((option) => {
        return pattern.test(option.name) || pattern.test(option.item_id);
      });
    },
    serviceLinks() {
      return this.$store.getters.serviceLinksForProduct(
        this.jobId,
        this.activeProductId,
      );
    },
    serviceDefinitions() {
      const sourceServiceDefinitions = this.$store.getters.serviceDefinitions(
        this.jobId,
      );
      return this.limitServiceDefinitionsMixin(
        sourceServiceDefinitions,
        this.serviceLinks,
      );
    },
    allServicesDefinitions() {
      return this.requiredServicesDefinitions.concat(
        this.optionalServicesDefinitions,
      );
    },
    requiredServicesDefinitions() {
      return this.serviceDefinitions[ServiceLinkTypeEnum.ONLY_WITH] ?? [];
    },
    optionalServicesDefinitions() {
      return this.serviceDefinitions[ServiceLinkTypeEnum.OPTIONAL] ?? [];
    },
    supplierType: {
      get() {
        return (
          this.activeProduct?.supplier ||
          this.activeProductSuppliers[0]?.defaultSupplierType
        );
      },
      set(value) {
        if (value == null) return;
        this.$store.commit(PRODUCTS_SELECTED_SET, {
          jobId: this.jobId,
          elementIndex: this.elementIndex,
          supplier: value,
          quantity: this.quantity,
        });
        this.$store.dispatch(PRODUCT_OPPORTUNITIES_FETCH, this.activeProductId);
      },
    },
    suppliers() {
      const suppliers = this.$store.getters.suppliers();
      return suppliers.map((sup) => ({
        id: sup.id,
        name: sup.name,
      }));
    },
    supplier: {
      get() {
        if (
          !this.activeProduct.supplier_sfid &&
          this.activeProductSuppliers.length > 0
        ) {
          return {
            id: this.activeProductSuppliers[0].id,
            name: this.activeProductSuppliers[0].name,
            pricePurchasing: this.activeProductSuppliers[0].pricePurchasing,
            priceSelling: this.activeProductSuppliers[0].priceSelling,
            priceListing: this.activeProductSuppliers[0].priceListing,
          };
        } else if (this.activeProduct.supplier_sfid) {
          return (
            this.activeProductSuppliers.find(
              ({ id }) => id === this.activeProduct.supplier_sfid,
            ) ?? {
              id: this.activeProduct.supplier_sfid,
              name: this.activeProduct.supplier_name,
            }
          );
        }
        return null;
      },
      set(value) {
        this.$store.commit(PRODUCTS_SELECTED_SET, {
          jobId: this.jobId,
          elementIndex: this.elementIndex,
          supplier: this.supplierType,
          supplier_name: value?.name,
          supplier_sfid: value?.id,
          quantity: this.quantity,
        });
      },
    },
    invoicingTarget: {
      get() {
        return (
          this.activeProduct.invoicing_target ||
          this.activeProductDefinition?.opportunities?.[0]?.invoicingTarget ||
          GeneralInvoicingTargetEnum.GID
        );
      },
      set(value) {
        if (value == null) return;
        this.$store.commit(PRODUCTS_SELECTED_SET, {
          jobId: this.jobId,
          elementIndex: this.elementIndex,
          invoicing_target: value,
        });
      },
    },
    productUrl() {
      return this.activeProductDefinition?.url;
    },
    serial_number: {
      get() {
        return this.activeProduct?.serial_number;
      },
      set(value) {
        this.$store.commit(PRODUCTS_SELECTED_SET, {
          jobId: this.jobId,
          elementIndex: this.elementIndex,
          serial_number: value,
        });
      },
    },
    chargepoint_number: {
      get() {
        return this.activeProduct?.chargepoint_number;
      },
      set(value) {
        this.$store.commit(PRODUCTS_SELECTED_SET, {
          jobId: this.jobId,
          elementIndex: this.elementIndex,
          chargepoint_number: value,
        });
      },
    },
    price_purchasing: {
      get() {
        return this.activeProduct?.price_purchasing === undefined
          ? this.supplier?.pricePurchasing
          : this.activeProduct?.price_purchasing;
      },
      set(value) {
        this.$store.commit(PRODUCTS_SELECTED_SET, {
          jobId: this.jobId,
          elementIndex: this.elementIndex,
          price_purchasing: !nullOrUndefined(value) ? Number(value) : null,
        });
      },
    },
    price_selling: {
      get() {
        return this.activeProduct?.price_selling === undefined
          ? this.supplier?.priceSelling
          : this.activeProduct?.price_selling;
      },
      set(value) {
        this.$store.commit(PRODUCTS_SELECTED_SET, {
          jobId: this.jobId,
          elementIndex: this.elementIndex,
          price_selling: !nullOrUndefined(value) ? Number(value) : null,
        });
      },
    },
    price_selling_style: {
      get() {
        if (
          this.invoicingTarget &&
          [
            GeneralInvoicingTargetEnum.AFFILIATE,
            GeneralInvoicingTargetEnum.BRAND,
            GeneralInvoicingTargetEnum.BRAND_1,
            GeneralInvoicingTargetEnum.BRAND_2,
          ].includes(this.invoicingTarget)
        ) {
          return { 'background-color': '#D4EDDA' };
        }
        return null;
      },
    },
    price_listing: {
      get() {
        return this.activeProduct?.price_listing === undefined
          ? this.supplier?.priceListing
          : this.activeProduct?.price_listing;
      },
      set(value) {
        this.$store.commit(PRODUCTS_SELECTED_SET, {
          jobId: this.jobId,
          elementIndex: this.elementIndex,
          price_listing: !nullOrUndefined(value) ? Number(value) : null,
        });
      },
    },
    price_listing_style: {
      get() {
        if (
          this.invoicingTarget &&
          this.invoicingTarget === GeneralInvoicingTargetEnum.CUSTOMER
        ) {
          return { 'background-color': '#D4EDDA' };
        }
        return null;
      },
    },
    quantity: {
      get() {
        return this.activeProduct?.quantity;
      },
      set(value) {
        let oldValue = this.quantity ?? 0;
        value = parseFloat(value);
        this.$store.commit(PRODUCTS_SELECTED_SET, {
          jobId: this.jobId,
          elementIndex: this.elementIndex,
          supplier: this.supplierType,
          quantity: value,
        });
        for (const autoService of this.requiredServicesDefinitions) {
          let serviceIndex = this.$store.getters.getServiceIndex({
            jobId: this.jobId,
            service: autoService.sfid,
            productId: undefined,
          });
          this.$store.dispatch(SERVICE_SELECTED_ADD_QUANTITY, {
            quantity: this.quantityForLinkedService(
              autoService.sfid,
              value - oldValue,
            ),
            serviceIndex,
            jobId: this.jobId,
          });
        }
      },
    },
    selectProduct: {
      get() {
        return this.productOptions.find(
          ({ id }) => +id === parseInt(this.activeProductId),
        );
      },
      set(product) {
        const job = this.$store.getters.jobEdit(this.jobId);
        const {
          pricePurchasing,
          priceSelling,
          priceListing,
          defaultProductSupplier,
          supplier_sfid,
          supplier_name,
          invoicingTarget,
        } = product.opportunities[0] ?? {};
        this.$store.dispatch(PRODUCTS_SELECTED_SET, {
          elementIndex: this.elementIndex,
          jobId: this.jobId,
          oemId: job.oemId,
          supplier: defaultProductSupplier,
          supplier_sfid: supplier_sfid || this.supplier.id,
          supplier_name: supplier_name,
          product: product?.id,
          price_purchasing:
            pricePurchasing !== null
              ? pricePurchasing
              : product.price_purchasing_net,
          price_selling:
            priceSelling !== null ? priceSelling : product.price_selling_net,
          price_listing:
            priceListing !== null ? priceListing : product.price_listing,
          invoicing_target: invoicingTarget,
        });
        if (this.supplierType === 'gid') {
          this.$store.dispatch(PRODUCT_OPPORTUNITIES_FETCH, product?.id);
        }
        for (const autoService of this.requiredServicesDefinitions) {
          this.$store.dispatch(SERVICE_SELECTED_ADD, {
            jobId: this.jobId,
            productId: this.activeProductId,
            serviceSfid: autoService.sfid,
            price_lists: autoService.price_lists,
            price_purchasing: autoService.price_purchasing,
            price_selling: autoService.price_selling,
            invoicing_target: autoService.invoicing_target,
            quantity: this.quantityForLinkedService(
              autoService.sfid,
              this.quantity,
            ),
          });
        }
      },
    },
    partiallySelection: function () {
      return loadPartialSelection(
        this.selections,
        SelectActionsEnum.PARTIAL_INVOICING,
      );
    },
  },
  methods: {
    searchProducts(query) {
      this.productFilter = query;
    },
    deleteSelectedProduct() {
      let payload = {
        elementIndex: this.elementIndex,
        jobId: this.jobId,
      };
      for (const autoSetService of this.allServicesDefinitions) {
        this.$store.dispatch(SERVICE_SELECTED_ITEM_UNREF, {
          jobId: this.jobId,
          productId: this.activeProductId,
          service: autoSetService.sfid,
          quantityDelta: this.quantityForLinkedService(
            autoSetService.sfid,
            -this.quantity,
          ),
        });
      }
      this.$store.dispatch(PRODUCTS_SELECTED_ITEM_DELETE, payload);
    },
    quantityForLinkedService(linkedServiceId, productQuantity) {
      const link = this.serviceLinks.find(
        (sl) => sl.service?.id === linkedServiceId,
      );
      if (link?.quantity) {
        return productQuantity * link?.quantity;
      }
    },
    onPartialInvoiceChange(
      isSelected,
      type = SelectActionsEnum.PARTIAL_INVOICING,
    ) {
      const productState = isSelected ? PartialStateEnum.SCHEDULED : null;
      this.$store.dispatch(PRODUCT_PRESELECT_AUTOMATION, {
        selected: isSelected,
        action: type,
        state: productState,
        item: this.activeProduct.product_id,
        productIndex: this.elementIndex,
        id: this.activeProduct.id,
        jobId: this.jobId,
      });
      this.$store.dispatch(SERVICE_PRESELECT_AUTOMATION_BY_PRODUCT, {
        productId: this.activeProduct.id,
        jobId: this.jobId,
        selected: isSelected,
      });
    },
  },
  watch: {
    activeProduct() {
      const previousProductId = this.previousProductId;
      this.previousProductId = this.activeProductId;
      if (previousProductId && this.previousProductId !== previousProductId) {
        this.$store.dispatch(SERVICE_SELECTED_REFRESH, {
          jobId: this.jobId,
          productId: previousProductId,
        });
      }
    },
  },
};
</script>
